import { omit } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { Flex } from "../../../../components/layout";
import { H2 } from "../../../../components/typography";
import { WIDGET_BORDER_RADIUS } from "../../../../components/widget";
import { BillableApi } from "../../../../store/billing/models/internal";
import { colours } from "../../../../styles/colours";
import { toDecimal } from "../../../../utils/utils";

export const amount = css`
  font-weight: 700;
  color: ${colours.darkBlueGrey};
`;

export const Wrapper = styled(Flex)`
  background: ${colours.lighterBlue};
  color: ${colours.primaryBlue};
  padding: 1em;
  font-size: 1.5em;
  border-radius: ${WIDGET_BORDER_RADIUS};

  flex-direction: column;
  justify-content: space-between;
`;

export const Title = styled(H2)`
  font-weight: 500;
  margin: 0 0 0.3em;
  font-size: 1.67em;
`;

export const TitleWrapper = styled.div`
  font-size: 0.67em;
  align-self: flex-start;
`;

export const CallSpan = styled.span`
  color: ${colours.darkGrey};
`;

export const Value = styled.p`
  ${amount};
  text-align: right;
  font-size: 2.5em;
  margin: 0.3em 0 0;
  align-self: flex-end;
`;

export const Free = styled.span`
  color: ${colours.green};
  font-weight: 300;
  align-self: flex-end;
  font-size: 0.9em;
`;

export type CardProps = {
  apiName: typeof BillableApi.values[number];
  freeCredits?: number;
  originalPrice: number;
  discount: number;
  credits: number;
};

export const CardWrapper: FC<CardProps> = ({
  apiName,
  originalPrice,
  freeCredits,
  children,
  discount,
}) => {
  const discountedPrice = useMemo(() => originalPrice * discount, [
    discount,
    originalPrice,
  ]);

  const formattedPrice = useMemo(() => toDecimal(discountedPrice), [
    discountedPrice,
  ]);

  const { t } = useTranslation("common");

  return (
    <Wrapper>
      <TitleWrapper>
        <Title>{apiName}</Title>
        {formattedPrice} {t("credit", { count: discountedPrice })}{" "}
        <CallSpan>/ {t("call")}</CallSpan>
      </TitleWrapper>
      {children}
      {freeCredits ? (
        <Free>
          +{freeCredits} {t("free")}
        </Free>
      ) : null}
    </Wrapper>
  );
};

export const DefaultCard: FC<CardProps> = (props) => {
  const availableCalls = useMemo(
    () => Math.floor(props.credits / (props.originalPrice * props.discount)),
    [props.credits, props.originalPrice, props.discount]
  );

  return (
    <CardWrapper {...omit(props, "availableCalls")}>
      <Value>{availableCalls}</Value>
    </CardWrapper>
  );
};
